import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Sparkles, FolderOpen, Loader2, Github, Lock, Search, FileArchive } from 'lucide-react';
import { useBackend } from '../services/backend';
import { useProjectStore } from '../stores/projectStore';
import { useSettingsStore } from '../stores/settingsStore';
import type { CreateProjectInput } from '../services/backend/types';
import type { GitHubRepo } from '../services/backend/IBackend';
import type { AgentType } from '../types/project';
import type { ProjectAnalysisResult } from '../types/tauri';
import AgentSelector from '../components/AgentSelector';
import FolderPicker from '../components/FolderPicker';
import { logger } from '@/lib/logger';
import { AGENT_CLI_CONFIG, defaultFlagsFor } from '@/lib/agentCliConfig';
import { setProjectTerminalSettings, DEFAULT_SHELL, type AteliercodeConfig, type AteliercodeRunners } from '@/lib/projectSettings';
import { detectRunConfigurations, type DetectionContext } from '@/lib/runConfigDetector';

interface FormData {
  name: string;
  path: string;
  description: string;
  agentType: AgentType | null;
  agentFlags: string[];
  customAgentFlags: string;
  initGit: boolean;
  /** ZIP mode only: absolute path of the chosen .zip file. */
  zipPath: string;
}

interface FormErrors {
  name?: string;
  path?: string;
  agentType?: string;
}

const NEW_PROJECT_STEPS = [
  { id: 1, label: 'Basic Info', description: 'Project name and location' },
  { id: 2, label: 'AI Agent', description: 'Choose your assistant' },
  { id: 3, label: 'Review', description: 'Confirm and create' },
];

const EXISTING_PROJECT_STEPS = [
  { id: 1, label: 'Select Folder', description: 'Choose existing project' },
  { id: 2, label: 'AI Agent', description: 'Choose your assistant' },
  { id: 3, label: 'AI Analysis', description: 'Analyzing project' },
  { id: 4, label: 'Review', description: 'Confirm and create' },
];

// GitHub mode = the existing-project flow with step 1 swapped for a repo
// picker + clone; once the clone lands, formData.path is a local folder and
// steps 2-4 behave exactly like 'existing'.
const GITHUB_PROJECT_STEPS = [
  { id: 1, label: 'GitHub Repo', description: 'Pick a repo and clone it' },
  { id: 2, label: 'AI Agent', description: 'Choose your assistant' },
  { id: 3, label: 'AI Analysis', description: 'Analyzing project' },
  { id: 4, label: 'Review', description: 'Confirm and create' },
];

// ZIP mode = the existing-project flow with step 1 swapped for a zip picker +
// extract. Once extracted, formData.path is a local folder and steps 2-4 behave
// exactly like 'existing' (analyze → AI name/description → review → create).
/**
 * Normalize any GitHub repo reference to `owner/repo` (or null if it isn't one).
 * Accepts: `owner/repo`, `https://github.com/owner/repo(.git)`,
 * `git@github.com:owner/repo.git`, `github.com/owner/repo`. Mirrors the core's
 * validate_repo char set so the clone command accepts it.
 */
function normalizeRepoSpec(input: string): string | null {
  let s = input.trim();
  if (!s) return null;
  s = s.replace(/\.git$/i, '');
  s = s.replace(/^git@[^:]+:/i, '').replace(/^[a-z][a-z0-9+.-]*:\/\//i, '').replace(/^github\.com\//i, '');
  const parts = s.split('/').filter(Boolean);
  if (parts.length < 2) return null;
  const owner = parts[parts.length - 2];
  const repo = parts[parts.length - 1];
  if (!/^[A-Za-z0-9._-]+$/.test(owner) || !/^[A-Za-z0-9._-]+$/.test(repo)) return null;
  return `${owner}/${repo}`;
}

const ZIP_PROJECT_STEPS = [
  { id: 1, label: 'Select ZIP', description: 'Pick a .zip and where it goes' },
  { id: 2, label: 'AI Agent', description: 'Choose your assistant' },
  { id: 3, label: 'AI Analysis', description: 'Analyzing project' },
  { id: 4, label: 'Review', description: 'Confirm and create' },
];

export default function ProjectWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backend = useBackend();
  const modeParam = searchParams.get('mode');
  const mode: 'new' | 'existing' | 'github' | 'zip' =
    modeParam === 'existing' ? 'existing'
    : modeParam === 'github' ? 'github'
    : modeParam === 'zip' ? 'zip'
    : 'new';
  /** Modes whose steps 2-4 share the analyze-an-existing-folder flow. */
  const folderMode = mode === 'existing' || mode === 'github' || mode === 'zip';
  const createProject = useProjectStore((state) => state.createProject);
  const isLoading = useProjectStore((state) => state.isLoading);

  const STEPS =
    mode === 'github' ? GITHUB_PROJECT_STEPS
    : mode === 'existing' ? EXISTING_PROJECT_STEPS
    : mode === 'zip' ? ZIP_PROJECT_STEPS
    : NEW_PROJECT_STEPS;

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    path: '',
    description: '',
    agentType: null,
    agentFlags: [],
    customAgentFlags: '',
    initGit: true,
    zipPath: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // ── Projects Root + ZIP mode state ────────────────────────────────
  const projectsRoot = useSettingsStore((s) => s.projects.root);
  /** Default parent dir for new projects: the global setting, else the most
   *  common parent of existing projects, else ''. */
  const effectiveRoot = useMemo(() => {
    if (projectsRoot.trim()) return projectsRoot.trim().replace(/\/+$/, '');
    const paths = useProjectStore.getState().projects.map((p) => p.root_path);
    const parents = new Map<string, number>();
    for (const p of paths) {
      const parent = p.replace(/\/+$/, '').split('/').slice(0, -1).join('/');
      if (parent) parents.set(parent, (parents.get(parent) ?? 0) + 1);
    }
    return [...parents.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
  }, [projectsRoot]);
  /** True once the user has hand-edited the ZIP target path — after that a zip
   *  pick no longer auto-fills it (their choice wins). */
  const [zipPathTouched, setZipPathTouched] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);

  // Seed the default project path from the Projects Root for new + zip modes.
  useEffect(() => {
    if ((mode === 'new' || mode === 'zip') && effectiveRoot) {
      setFormData((prev) => (prev.path ? prev : { ...prev, path: effectiveRoot }));
    }
  }, [mode, effectiveRoot]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<ProjectAnalysisResult | null>(null);

  // ── GitHub mode state ─────────────────────────────────────────────
  const [repos, setRepos] = useState<GitHubRepo[] | null>(null);
  const [reposError, setReposError] = useState<string | null>(null);
  const [repoSearch, setRepoSearch] = useState('');
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepo | null>(null);
  /** Free-form repo to clone that isn't in the user's own list — a GitHub URL,
   *  SSH remote, or owner/repo. When set, it wins over the picked repo. */
  const [customRepo, setCustomRepo] = useState('');
  /** Parent directory the repo gets cloned INTO (clone dir = dest/<repoName>). */
  const [cloneParent, setCloneParent] = useState('');
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // Load the user's repos when entering GitHub mode; default the clone
  // destination to wherever their projects already live (most common parent
  // dir of registered projects).
  useEffect(() => {
    if (mode !== 'github') return;
    if (!backend.github) {
      setReposError('GitHub import needs the desktop app (the gh CLI runs on your machine).');
      setRepos([]);
      return;
    }
    backend.github.listRepos()
      .then(setRepos)
      .catch((e) => {
        setReposError(e instanceof Error ? e.message : String(e));
        setRepos([]);
      });
    if (effectiveRoot) setCloneParent((cur) => cur || effectiveRoot);
  }, [mode, backend, effectiveRoot]);

  /** The repo to clone as `owner/repo`: a pasted URL/owner-repo wins over the
   *  picked-from-your-list repo. `null` when nothing valid is chosen. */
  const repoSpec = customRepo.trim()
    ? normalizeRepoSpec(customRepo)
    : (selectedRepo?.nameWithOwner ?? null);
  const repoName = repoSpec ? repoSpec.split('/')[1] : '';
  /** Where the clone will land for the chosen repo. */
  const cloneDest = repoSpec && cloneParent
    ? `${cloneParent.replace(/\/+$/, '')}/${repoName}`
    : '';

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user types
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (mode === 'github') {
      // GitHub clone validation — step 1 needs a repo + destination; the
      // cloned path fills formData.path before step 2 is reachable.
      if (step === 1) {
        if (customRepo.trim() && !normalizeRepoSpec(customRepo)) {
          newErrors.path = "That doesn't look like a repo — use a GitHub URL or owner/repo";
        } else if (!repoSpec) {
          newErrors.path = 'Pick a repository, or paste a URL to clone';
        } else if (!cloneParent.trim()) {
          newErrors.path = 'Choose where to store the project';
        }
      }
      if (step === 2 && !formData.agentType) {
        newErrors.agentType = 'Please select an AI agent';
      }
      if (step === 4 && !formData.name.trim()) {
        newErrors.name = 'Project name is required';
      }
    } else if (mode === 'zip') {
      // ZIP import validation — step 1 needs a zip + a target path; extraction
      // in handleNext fills formData.path with the extracted folder for step 2+.
      if (step === 1) {
        if (!formData.zipPath.trim()) {
          newErrors.path = 'Choose a .zip file to import';
        } else if (!formData.path.trim()) {
          newErrors.path = 'Choose where the project should go';
        }
      }
      if (step === 2 && !formData.agentType) {
        newErrors.agentType = 'Please select an AI agent';
      }
      if (step === 4 && !formData.name.trim()) {
        newErrors.name = 'Project name is required';
      }
    } else if (mode === 'existing') {
      // Existing project validation
      if (step === 1) {
        if (!formData.path.trim()) {
          newErrors.path = 'Please select a project folder';
        }
      }

      if (step === 2) {
        if (!formData.agentType) {
          newErrors.agentType = 'Please select an AI agent';
        }
      }

      if (step === 4) {
        if (!formData.name.trim()) {
          newErrors.name = 'Project name is required';
        }
      }
    } else {
      // New project validation
      if (step === 1) {
        if (!formData.name.trim()) {
          newErrors.name = 'Project name is required';
        } else if (formData.name.length < 3) {
          newErrors.name = 'Project name must be at least 3 characters';
        }

        if (!formData.path.trim()) {
          newErrors.path = 'Project location is required';
        }
      }

      if (step === 2) {
        if (!formData.agentType) {
          newErrors.agentType = 'Please select an AI agent';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;

    // GitHub step 1 → the clone happens HERE, between picking and the agent
    // step, so by step 2 the project is a plain local folder and the rest of
    // the wizard (analysis, review, create) is the existing-project flow.
    if (mode === 'github' && currentStep === 1) {
      if (!backend.github || !repoSpec || !cloneDest) return;
      setCloning(true);
      setCloneError(null);
      try {
        const { path } = await backend.github.clone(repoSpec, cloneDest);
        updateFormData('path', path);
        setCurrentStep(2);
      } catch (e) {
        setCloneError(e instanceof Error ? e.message : String(e));
      } finally {
        setCloning(false);
      }
      return;
    }

    // ZIP step 1 → extract HERE (like the github clone). Extraction resolves the
    // project folder per the existence rule; from step 2 on it's a plain local
    // folder and the rest of the wizard is the existing-project flow.
    if (mode === 'zip' && currentStep === 1) {
      setExtracting(true);
      setExtractError(null);
      try {
        const dir = await backend.projects.extractZip(formData.zipPath, formData.path);
        updateFormData('path', dir);
        setCurrentStep(2);
      } catch (e) {
        setExtractError(e instanceof Error ? e.message : String(e));
      } finally {
        setExtracting(false);
      }
      return;
    }

    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // AI Analysis for existing projects
  const analyzeExistingProject = async () => {
    setIsAnalyzing(true);

    try {
      // First, do basic directory analysis for file stats and git detection
      const basicAnalysis = await backend.projects.analyze(formData.path);
      setAiAnalysis(basicAnalysis);
      updateFormData('initGit', !basicAnalysis.has_git);

      // Then use the same AI generation logic as "Regenerate with AI" in settings
      // This provides better, more contextual names and descriptions
      const aiDetails = await backend.projects.generateDetails(formData.path);

      // Update with AI-generated name and description
      updateFormData('name', aiDetails.name);
      updateFormData('description', aiDetails.description);

      // Update analysis with AI suggestions for display
      setAiAnalysis(prev => prev ? {
        ...prev,
        suggested_name: aiDetails.name,
        suggested_description: aiDetails.description,
      } : basicAnalysis);
    } catch (error) {
      logger.error('ProjectWizard', 'Failed to analyze project with AI:', error);
      // Fallback to basic analysis if AI fails
      try {
        const basicAnalysis = await backend.projects.analyze(formData.path);
        setAiAnalysis(basicAnalysis);
        updateFormData('name', basicAnalysis.suggested_name);
        updateFormData('description', basicAnalysis.suggested_description);
        updateFormData('initGit', !basicAnalysis.has_git);
      } catch (fallbackError) {
        logger.error('ProjectWizard', 'Basic analysis also failed:', fallbackError);
        // Show a generic error but continue - user can still fill manually
        setAiAnalysis({
          suggested_name: formData.path.split(/[/\\]/).pop() || 'my-project',
          suggested_description: 'Unable to analyze project automatically. Please fill in the details manually.',
          detected_languages: [],
          detected_frameworks: [],
          file_count: 0,
          has_git: false,
        });
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Trigger analysis when moving to step 3 in existing/github mode (a cloned
  // repo is just an existing folder by this point).
  useEffect(() => {
    if (folderMode && currentStep === 3 && !aiAnalysis && formData.agentType) {
      analyzeExistingProject();
    }
  }, [currentStep, folderMode, formData.agentType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(currentStep)) {
      return;
    }

    if (!formData.agentType) {
      return;
    }

    try {
      const projectInput: CreateProjectInput = {
        name: formData.name,
        root_path: formData.path,
        description: formData.description || undefined,
        agent_type: formData.agentType as string,
        initialize_git: formData.initGit,
      };

      const project = await createProject(projectInput);

      // Save agent flags as initial project settings, and mark this as a
      // brand-new project (agentHasLaunched: false) so its FIRST agent launch
      // starts a fresh session — no --continue — even though Continue is a
      // saved default (see TerminalTab + buildAgentCommand).
      const settings = setProjectTerminalSettings(null, {
        autoLaunchAgent: true,
        agentFlags: formData.agentFlags,
        customAgentFlags: formData.customAgentFlags,
        agentHasLaunched: false,
      });
      await useProjectStore.getState().updateProject(project.id, { settings });

      // Auto-generate .ateliercode/ with config.json + runners.json
      try {
        const config: AteliercodeConfig = {
          $schema: 'https://ateliercode.dev/schemas/config.v1.json',
          agent: {
            type: formData.agentType as string,
            autoLaunch: true,
            flags: formData.agentFlags,
            ...(formData.customAgentFlags.trim() ? { customFlags: formData.customAgentFlags.trim() } : {}),
          },
          // Platform-aware (bash on Linux/macOS, pwsh on Windows). This used
          // to be a hardcoded 'powershell', which is how every Linux project
          // ended up requesting a shell that doesn't exist here.
          terminal: { defaultShell: DEFAULT_SHELL },
        };

        // Auto-detect run configurations from project files
        let detectedConfigs: Array<{ name: string; command: string }> = [];
        try {
          const readFile = (path: string) => backend.files.readFile(project.id, path);
          const rootNodes = await backend.files.getFolderChildren(project.id, formData.path);
          const ctx: DetectionContext = { readFile, rootFiles: rootNodes.map((n) => n.name) };
          const detected = await detectRunConfigurations(ctx);
          detectedConfigs = detected.map((c) => ({ name: c.name, command: c.command }));
        } catch {
          // Detection failed — that's OK, create empty runners
        }

        const runners: AteliercodeRunners = {
          $schema: 'https://ateliercode.dev/schemas/runners.v1.json',
          configs: detectedConfigs,
          default: detectedConfigs[0]?.name ?? null,
        };

        await backend.files.initAteliercodeConfig(
          project.id,
          JSON.stringify(config, null, 2),
          JSON.stringify(runners, null, 2),
        );
        logger.info('ProjectWizard', `Generated .ateliercode/ for ${formData.name} with ${detectedConfigs.length} run configs`);
      } catch (e) {
        logger.warn('ProjectWizard', 'Failed to generate .ateliercode/ config', e);
        // Non-fatal — project still created successfully
      }

      // Navigate to the workspace
      navigate(`/workspace/${project.id}`);
    } catch (error) {
      logger.error('ProjectWizard', 'Failed to create project:', error);
      // Error is handled in the store
    }
  };

  // GitHub step 1: repo picker + clone destination. Everything after the
  // clone reuses the existing-project steps.
  const renderGithubRepoStep = () => {
    const q = repoSearch.trim().toLowerCase();
    const filtered = (repos ?? []).filter(
      (r) =>
        !q ||
        r.nameWithOwner.toLowerCase().includes(q) ||
        (r.description ?? '').toLowerCase().includes(q),
    );
    return (
      <div className="space-y-5">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
              <Github className="w-8 h-8 text-primary" />
            </div>
          </div>
          <h2 className="text-3xl font-bold mb-2">Clone from GitHub</h2>
          <p className="text-base-content/70">
            Paste any repo's URL, or pick one of yours — we'll clone it and set it up
          </p>
        </div>

        {/* Any repo (not just yours): a URL / SSH remote / owner/repo. */}
        <div className="form-control">
          <label className="label"><span className="label-text">Repository URL or owner/repo</span></label>
          <label className="input input-bordered flex items-center gap-2">
            <Github className="w-4 h-4 text-base-content/40" />
            <input
              type="text"
              className="grow"
              placeholder="https://github.com/owner/repo   ·   or   owner/repo"
              value={customRepo}
              onChange={(e) => { setCustomRepo(e.target.value); setCloneError(null); }}
            />
          </label>
          {customRepo.trim() && (
            repoSpec
              ? <span className="label-text-alt text-base-content/50 mt-1">Will clone <code className="font-mono">{repoSpec}</code></span>
              : <span className="label-text-alt text-error mt-1">Use a GitHub URL or owner/repo</span>
          )}
        </div>

        <div className="divider text-xs text-base-content/40 my-1">or pick one of yours</div>

        {reposError ? (
          <div className="alert alert-warning text-sm">
            <span>{reposError}</span>
          </div>
        ) : repos === null ? (
          <div className="flex items-center justify-center gap-2 py-6 text-base-content/60">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading your repositories…
          </div>
        ) : (
          <div className={customRepo.trim() ? 'opacity-40 pointer-events-none' : ''}>
            <label className="input input-bordered flex items-center gap-2">
              <Search className="w-4 h-4 text-base-content/40" />
              <input
                type="text"
                className="grow"
                placeholder={`Search ${repos.length} repositories…`}
                value={repoSearch}
                onChange={(e) => setRepoSearch(e.target.value)}
              />
            </label>
            <div className="max-h-56 mt-3 overflow-y-auto border border-base-300 rounded-lg divide-y divide-base-300">
              {filtered.length === 0 ? (
                <div className="py-8 text-center text-sm text-base-content/40">No repositories match.</div>
              ) : (
                filtered.map((r) => (
                  <button
                    key={r.nameWithOwner}
                    type="button"
                    onClick={() => { setSelectedRepo(r); setCloneError(null); }}
                    className={`w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors ${
                      selectedRepo?.nameWithOwner === r.nameWithOwner
                        ? 'bg-primary/10 ring-1 ring-inset ring-primary'
                        : 'hover:bg-base-200'
                    }`}
                  >
                    <Github className="w-4 h-4 mt-0.5 text-base-content/40 shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{r.nameWithOwner}</span>
                        {r.isPrivate && (
                          <span className="inline-flex items-center gap-1 badge badge-xs badge-ghost">
                            <Lock className="w-2.5 h-2.5" /> private
                          </span>
                        )}
                      </div>
                      {r.description && (
                        <div className="text-xs text-base-content/50 truncate">{r.description}</div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        <FolderPicker
          value={cloneParent}
          onChange={setCloneParent}
          label="Store the project in"
          required
        />
        {cloneDest && (
          <div className="text-sm text-base-content/60">
            Will clone to <code className="font-mono text-base-content/80">{cloneDest}</code>
          </div>
        )}
        {errors.path && <div className="text-sm text-error">{errors.path}</div>}
        {cloneError && (
          <div className="alert alert-error text-sm">
            <span>{cloneError}</span>
          </div>
        )}
      </div>
    );
  };

  // ZIP step 1: pick a .zip (native file picker) + choose the project path.
  const chooseZip = async () => {
    setExtractError(null);
    const file = await backend.files.pickFile({
      title: 'Select a ZIP file',
      filterName: 'Zip archives',
      extensions: ['zip'],
    });
    if (!file) return;
    updateFormData('zipPath', file);
    // Smart path: auto-fill root/<zip-name> ONLY while the user hasn't
    // hand-edited the path (their explicit choice always wins).
    if (!zipPathTouched && effectiveRoot) {
      const stem = (file.split(/[/\\]/).pop() || '').replace(/\.zip$/i, '');
      if (stem) updateFormData('path', `${effectiveRoot.replace(/\/+$/, '')}/${stem}`);
    }
  };

  const renderZipStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <FileArchive className="w-8 h-8 text-primary" />
          </div>
        </div>
        <h2 className="text-3xl font-bold mb-2">Import from ZIP</h2>
        <p className="text-base-content/70">Pick a .zip and choose where the project should live</p>
      </div>

      <div className="form-control">
        <label className="label"><span className="label-text">ZIP file</span></label>
        <div className="flex items-center gap-2">
          <div className="flex-1 truncate text-sm px-3 py-2 rounded-lg bg-base-200 border border-base-300 min-h-10 flex items-center">
            {formData.zipPath
              ? <span className="flex items-center gap-2 truncate"><FileArchive className="w-4 h-4 text-primary shrink-0" /><span className="truncate">{formData.zipPath}</span></span>
              : <span className="text-base-content/40">No file chosen</span>}
          </div>
          <button type="button" onClick={chooseZip} className="btn btn-outline gap-1">
            <FolderOpen className="w-4 h-4" /> Browse…
          </button>
        </div>
      </div>

      <FolderPicker
        value={formData.path}
        onChange={(p) => { setZipPathTouched(true); updateFormData('path', p); }}
        error={errors.path}
        required
        label="Project path"
        placeholder={effectiveRoot ? `${effectiveRoot}/my-project` : 'e.g. ~/projects/my-project'}
      />
      <p className="text-xs text-base-content/50 -mt-4">
        A new path is created and used as the project folder. An existing folder gets a
        subfolder named after the zip.
      </p>

      {extractError && (
        <div className="alert alert-error text-sm"><span>{extractError}</span></div>
      )}
    </div>
  );

  const renderStepContent = () => {
    // Existing-folder flow (also everything after the GitHub clone)
    if (folderMode) {
      switch (currentStep) {
        case 1:
          if (mode === 'github') return renderGithubRepoStep();
          if (mode === 'zip') return renderZipStep();
          return (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                    <FolderOpen className="w-8 h-8 text-primary" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold mb-2">Add Existing Project</h2>
                <p className="text-base-content/70">
                  Select a folder containing your existing code
                </p>
              </div>

              {/* Project Location */}
              <FolderPicker
                value={formData.path}
                onChange={(path) => updateFormData('path', path)}
                error={errors.path}
                required
                label="Existing Project Folder"
              />

              <div className="alert alert-info">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span>Our AI will analyze your project and suggest details automatically</span>
              </div>
            </div>
          );

        case 2:
          return (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-2">Choose Your AI Agent</h2>
                <p className="text-base-content/70">
                  Select the AI assistant to help with this project
                </p>
              </div>

              <AgentSelector
                value={formData.agentType}
                onChange={(agent) => {
                  updateFormData('agentType', agent);
                  setFormData((prev) => ({ ...prev, agentFlags: defaultFlagsFor(agent), customAgentFlags: '' }));
                }}
                error={errors.agentType}
              />

              {formData.agentType && AGENT_CLI_CONFIG[formData.agentType] && (
                <div className="card bg-base-200">
                  <div className="card-body py-4">
                    <h4 className="font-medium text-sm mb-1">Launch Options</h4>
                    <p className="text-xs text-base-content/50 mb-3">These will be saved as project defaults.</p>
                    {AGENT_CLI_CONFIG[formData.agentType].flags.map((flag) => (
                      <label key={flag.id} className="label cursor-pointer justify-start gap-3 py-1">
                        <input
                          type="checkbox"
                          checked={formData.agentFlags.includes(flag.id)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...formData.agentFlags, flag.id]
                              : formData.agentFlags.filter((id) => id !== flag.id);
                            setFormData((prev) => ({ ...prev, agentFlags: updated }));
                          }}
                          className="checkbox checkbox-sm checkbox-primary"
                        />
                        <div>
                          <span className="label-text text-sm">{flag.label}</span>
                          <p className="text-xs text-base-content/60">{flag.description}</p>
                        </div>
                      </label>
                    ))}
                    <div className="form-control mt-3">
                      <label className="label py-1">
                        <span className="label-text text-sm">Additional flags</span>
                        <span className="label-text-alt text-xs text-base-content/60">appended verbatim</span>
                      </label>
                      <input
                        type="text"
                        value={formData.customAgentFlags}
                        onChange={(e) => setFormData((prev) => ({ ...prev, customAgentFlags: e.target.value }))}
                        placeholder={`e.g. --model gpt-5-codex --cd ${formData.path || '.'}`}
                        className="input input-bordered input-sm font-mono"
                      />
                      <label className="label py-1">
                        <span className="label-text-alt text-xs text-base-content/50">
                          Anything you'd type after <code>{AGENT_CLI_CONFIG[formData.agentType].command}</code> on the command line.
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );

        case 3:
          return (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  <div className={`w-16 h-16 rounded-full ${isAnalyzing ? 'bg-primary/20 animate-pulse' : 'bg-success/20'} flex items-center justify-center`}>
                    {isAnalyzing ? (
                      <Loader2 className="w-8 h-8 text-primary animate-spin" />
                    ) : (
                      <Sparkles className="w-8 h-8 text-success" />
                    )}
                  </div>
                </div>
                <h2 className="text-3xl font-bold mb-2">
                  {isAnalyzing ? 'Analyzing Project...' : 'Analysis Complete!'}
                </h2>
                <p className="text-base-content/70">
                  {isAnalyzing
                    ? 'AI is examining your project structure, code, and dependencies'
                    : 'Here\'s what we discovered about your project'}
                </p>
              </div>

              {isAnalyzing ? (
                <div className="card bg-base-200">
                  <div className="card-body">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <span className="loading loading-spinner loading-sm text-primary"></span>
                        <span>Scanning files and directories...</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="loading loading-spinner loading-sm text-primary"></span>
                        <span>Detecting languages and frameworks...</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="loading loading-spinner loading-sm text-primary"></span>
                        <span>Analyzing project structure...</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="loading loading-spinner loading-sm text-primary"></span>
                        <span>Generating description...</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : aiAnalysis && (
                <div className="space-y-4">
                  <div className="card bg-base-200">
                    <div className="card-body">
                      <h3 className="card-title text-lg mb-4">Project Analysis</h3>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="stat bg-base-300 rounded-lg p-4">
                          <div className="stat-title text-xs">Files Found</div>
                          <div className="stat-value text-2xl">{aiAnalysis.file_count}</div>
                        </div>
                        <div className="stat bg-base-300 rounded-lg p-4">
                          <div className="stat-title text-xs">Git Repository</div>
                          <div className="stat-value text-2xl">{aiAnalysis.has_git ? '✓' : '✗'}</div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <div className="text-sm text-base-content/60 mb-2">Detected Languages</div>
                          <div className="flex flex-wrap gap-2">
                            {aiAnalysis.detected_languages.map(lang => (
                              <div key={lang} className="badge badge-primary">{lang}</div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm text-base-content/60 mb-2">Detected Frameworks</div>
                          <div className="flex flex-wrap gap-2">
                            {aiAnalysis.detected_frameworks.map(fw => (
                              <div key={fw} className="badge badge-secondary">{fw}</div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="alert alert-success">
                    <Sparkles className="w-5 h-5" />
                    <span>AI has auto-filled the project details below. Feel free to edit them!</span>
                  </div>
                </div>
              )}
            </div>
          );

        case 4:
          return (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                    <Check className="w-8 h-8 text-primary" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold mb-2">Review & Customize</h2>
                <p className="text-base-content/70">
                  Verify AI suggestions and adjust if needed
                </p>
              </div>

              <div className="card bg-base-200">
                <div className="card-body">
                  <h3 className="card-title text-lg mb-4">Project Details</h3>

                  <div className="space-y-4">
                    {/* Editable Project Name */}
                    <div className="form-control w-full">
                      <label className="label">
                        <span className="label-text font-medium">
                          Project Name
                          <span className="text-error ml-1">*</span>
                        </span>
                        <span className="label-text-alt badge badge-sm badge-success">AI Suggested</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => updateFormData('name', e.target.value)}
                        className="input input-bordered w-full"
                      />
                    </div>

                    <div className="divider my-2"></div>

                    <div>
                      <div className="text-sm text-base-content/60 mb-1">Location</div>
                      <div className="font-medium font-mono text-sm">{formData.path}</div>
                    </div>

                    <div className="divider my-2"></div>

                    {/* Editable Description */}
                    <div className="form-control w-full">
                      <label className="label">
                        <span className="label-text font-medium">Description</span>
                        <span className="label-text-alt badge badge-sm badge-success">AI Suggested</span>
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => updateFormData('description', e.target.value)}
                        className="textarea textarea-bordered w-full h-24"
                      />
                    </div>

                    <div className="divider my-2"></div>

                    <div>
                      <div className="text-sm text-base-content/60 mb-1">AI Agent</div>
                      <div className="font-medium">
                        {formData.agentType?.split('-').map(word =>
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </div>
                    </div>

                    {!aiAnalysis?.has_git && (
                      <>
                        <div className="divider my-2"></div>
                        <div className="form-control">
                          <label className="label cursor-pointer justify-start gap-3">
                            <input
                              type="checkbox"
                              checked={formData.initGit}
                              onChange={(e) => updateFormData('initGit', e.target.checked)}
                              className="checkbox checkbox-primary"
                            />
                            <div>
                              <span className="label-text font-medium">Initialize Git repository</span>
                              <p className="text-sm text-base-content/60">
                                No git repository detected
                              </p>
                            </div>
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );

        default:
          return null;
      }
    }

    // For new projects (original flow)
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-2">Create New Project</h2>
              <p className="text-base-content/70">
                Let's set up your AI-assisted development workspace
              </p>
            </div>

            {/* Project Name */}
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">
                  Project Name
                  <span className="text-error ml-1">*</span>
                </span>
              </label>
              <input
                type="text"
                placeholder="my-awesome-project"
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
                className={`input input-bordered w-full ${
                  errors.name ? 'input-error' : ''
                }`}
                autoFocus
              />
              {errors.name && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.name}</span>
                </label>
              )}
            </div>

            {/* Project Location */}
            <FolderPicker
              value={formData.path}
              onChange={(path) => updateFormData('path', path)}
              error={errors.path}
              required
            />

            {/* Project Description */}
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Description (Optional)</span>
              </label>
              <textarea
                placeholder="What is this project about?"
                value={formData.description}
                onChange={(e) => updateFormData('description', e.target.value)}
                className="textarea textarea-bordered w-full h-24"
              />
            </div>

            {/* Git Initialization */}
            <div className="form-control">
              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  checked={formData.initGit}
                  onChange={(e) => updateFormData('initGit', e.target.checked)}
                  className="checkbox checkbox-primary"
                />
                <div>
                  <span className="label-text font-medium">Initialize Git repository</span>
                  <p className="text-sm text-base-content/60">
                    Recommended for version control and collaboration
                  </p>
                </div>
              </label>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-2">Choose Your AI Agent</h2>
              <p className="text-base-content/70">
                Select the AI assistant that best fits your workflow
              </p>
            </div>

            <AgentSelector
              value={formData.agentType}
              onChange={(agent) => {
                updateFormData('agentType', agent);
                setFormData((prev) => ({ ...prev, agentFlags: defaultFlagsFor(agent), customAgentFlags: '' }));
              }}
              error={errors.agentType}
            />

            {formData.agentType && AGENT_CLI_CONFIG[formData.agentType] && (
              <div className="card bg-base-200">
                <div className="card-body py-4">
                  <h4 className="font-medium text-sm mb-1">Launch Options</h4>
                  <p className="text-xs text-base-content/50 mb-3">These will be saved as project defaults.</p>
                  {AGENT_CLI_CONFIG[formData.agentType].flags.map((flag) => (
                    <label key={flag.id} className="label cursor-pointer justify-start gap-3 py-1">
                      <input
                        type="checkbox"
                        checked={formData.agentFlags.includes(flag.id)}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...formData.agentFlags, flag.id]
                            : formData.agentFlags.filter((id) => id !== flag.id);
                          setFormData((prev) => ({ ...prev, agentFlags: updated }));
                        }}
                        className="checkbox checkbox-sm checkbox-primary"
                      />
                      <div>
                        <span className="label-text text-sm">{flag.label}</span>
                        <p className="text-xs text-base-content/60">{flag.description}</p>
                      </div>
                    </label>
                  ))}
                  <div className="form-control mt-3">
                    <label className="label py-1">
                      <span className="label-text text-sm">Additional flags</span>
                      <span className="label-text-alt text-xs text-base-content/60">appended verbatim</span>
                    </label>
                    <input
                      type="text"
                      value={formData.customAgentFlags}
                      onChange={(e) => setFormData((prev) => ({ ...prev, customAgentFlags: e.target.value }))}
                      placeholder={`e.g. --model gpt-5-codex --cd ${formData.path || '.'}`}
                      className="input input-bordered input-sm font-mono"
                    />
                    <label className="label py-1">
                      <span className="label-text-alt text-xs text-base-content/50">
                        Anything you'd type after <code>{AGENT_CLI_CONFIG[formData.agentType].command}</code> on the command line.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
              </div>
              <h2 className="text-3xl font-bold mb-2">Ready to Create</h2>
              <p className="text-base-content/70">
                Review your project configuration
              </p>
            </div>

            <div className="card bg-base-200">
              <div className="card-body">
                <h3 className="card-title text-lg mb-4">Project Details</h3>

                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-base-content/60 mb-1">Project Name</div>
                    <div className="font-medium">{formData.name}</div>
                  </div>

                  <div className="divider my-2"></div>

                  <div>
                    <div className="text-sm text-base-content/60 mb-1">Location</div>
                    <div className="font-medium font-mono text-sm">{formData.path}</div>
                  </div>

                  {formData.description && (
                    <>
                      <div className="divider my-2"></div>
                      <div>
                        <div className="text-sm text-base-content/60 mb-1">Description</div>
                        <div className="text-sm">{formData.description}</div>
                      </div>
                    </>
                  )}

                  <div className="divider my-2"></div>

                  <div>
                    <div className="text-sm text-base-content/60 mb-1">AI Agent</div>
                    <div className="font-medium">
                      {formData.agentType?.split('-').map(word =>
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </div>
                  </div>

                  <div className="divider my-2"></div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.initGit}
                      readOnly
                      className="checkbox checkbox-sm"
                    />
                    <span className="text-sm">Initialize Git repository</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="alert alert-info">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span>Click "Create Project" to start your AI-assisted development workspace</span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-base-100">
      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        {/* Scrollable content: on short viewports this area scrolls so the
            pinned footer (Back / Next / Create) always stays reachable. */}
        <div className="flex-1 min-h-0 overflow-y-auto px-8 py-8">
        {/* Progress Steps */}
        <ul className="steps steps-horizontal w-full mb-12">
          {STEPS.map((step) => (
            <li
              key={step.id}
              className={`step ${currentStep >= step.id ? 'step-primary' : ''}`}
              data-content={currentStep > step.id ? '✓' : step.id}
            >
              <div className="text-left mt-2">
                <div className="font-medium">{step.label}</div>
                <div className="text-xs text-base-content/60">{step.description}</div>
              </div>
            </li>
          ))}
        </ul>

        {/* Step Content */}
          <div className="mb-8">{renderStepContent()}</div>
        </div>

          {/* Navigation Buttons — pinned below the scroll area, always visible */}
          <div className="flex justify-between items-center px-8 py-4 border-t border-base-300 bg-base-100">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="btn btn-ghost gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <div className="text-sm text-base-content/60">
              Step {currentStep} of {STEPS.length}
            </div>

            {currentStep < STEPS.length ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={cloning || extracting}
                className="btn btn-primary gap-2"
              >
                {cloning || extracting ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    {extracting ? 'Extracting…' : 'Cloning…'}
                  </>
                ) : (
                  <>
                    {mode === 'github' && currentStep === 1 ? 'Clone & Continue'
                      : mode === 'zip' && currentStep === 1 ? 'Extract & Continue'
                      : 'Next'}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            ) : (
              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary gap-2"
              >
                {isLoading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Create Project
                  </>
                )}
              </button>
            )}
          </div>
        </form>
    </div>
  );
}
